import { SaveState } from "models/enumerations/save-state";
import { ImageBlockConfigurationRecord } from "models/view-models/block-configurations/image-block-configuration-record";
import { PageBlockEditorProps } from "organisms/page-blocks/active-page-block/page-block-editor";
import React, { useEffect, useMemo, useState } from "react";
import ImageBlockConfigurationService, {
    ImageBlockConfigurationUpdatePathParams,
    ImageBlockConfigurationUpdatePutParams,
} from "utilities/services/block-configurations/image-block-configuration-service";
import { RecordUtils, StringUtils } from "@rsm-hcd/javascript-core";
import FileRecord from "models/view-models/file-record";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import MimeTypes from "utilities/enumerations/mime-type";
import FileUtils from "utilities/file-utils";
import ImageEditorToolbar from "molecules/page-block-content/image-block/image-editor-toolbar";
import { t } from "utilities/localization-utils";
import { ImageSize } from "constants/notebooks/image-size";
import { ImagePosition } from "constants/notebooks/image-position";
import { useImageStyles } from "molecules/page-block-content/image-block/use-image-styles";
import useUserUploadId from "utilities/hooks/domain/image-upload/use-user-upload-id";
import ImageBlockUploadField from "molecules/form-fields/image-block-upload-field";
import ImageAnnotationToolbar from "molecules/page-block-content/image-block/image-annotation-toolbar";
import { ImageAnnotationRecord } from "models/view-models/block-configurations/image-annotation-record";
import ImageAnnotationsModal from "organisms/image-annotations/image-annotations-modal";
import useManageImageAnnotations from "organisms/image-annotations/use-manage-image-annotations";

interface ImageBlockEditorProps
    extends Pick<
        PageBlockEditorProps,
        | "block"
        | "onBlockSaving"
        | "onBlockSaved"
        | "onDone"
        | "onDelete"
        | "onMoveDown"
        | "onMoveUp"
    > {}

enum EditMode {
    Image,
    Annotation,
}

const CSS_CLASS_NAME = "c-image-block-editor";

const ImageBlockEditor: React.FC<ImageBlockEditorProps> = ({
    block,
    onBlockSaving,
    onBlockSaved,
    onDone,
    onDelete,
    onMoveDown,
    onMoveUp,
}: ImageBlockEditorProps) => {
    const { update: updateConfigurationApi } =
        ImageBlockConfigurationService.useUpdate();
    const [fileError, setFileError] = useState("");
    const [imageFile, setImageFile] = useState<FileRecord>();
    const [, setClearTrigger] = useState(false);
    const [size, setSize] = useState<ImageSize>();
    const [position, setPosition] = useState<ImagePosition>();

    const [replaceImageMode, setReplaceImageMode] = useState(false);
    const [editMode, setEditMode] = useState<EditMode>(EditMode.Image);
    const imageBlockConfiguration = useMemo(() => {
        const configuration = RecordUtils.ensureRecord(
            block.configuration,
            ImageBlockConfigurationRecord
        );

        setImageFile(configuration.imageFile ?? undefined);
        return configuration;
    }, [block.configuration]);

    const { sizeOrDefault, positionOrDefault } = useImageStyles(size, position);

    const {
        activeImageAnnotation,
        handleStartNewImageAnnotation,
        showImageAnnotationModal,
        closeImageAnnotationModal,
    } = useManageImageAnnotations(imageBlockConfiguration);

    useEffect(() => {
        setSize(imageBlockConfiguration.size);
        setPosition(imageBlockConfiguration.position);
    }, [imageBlockConfiguration]);

    // Updates the block-configuration to its viewport-based default.
    // This allows us to 'default' new ImageBlocks to have a small image on mobile even when
    // the default ImageBlock config includes Medium (default on desktop)
    useEffect(() => {
        if (!imageFile && imageBlockConfiguration.size !== sizeOrDefault) {
            handleSizeChange(sizeOrDefault);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageBlockConfiguration.size, imageFile, sizeOrDefault]);

    const [, setSaveState] = useState<SaveState>(SaveState.NotSaving);

    const { userUploadId } = useUserUploadId();

    const handleClear = () => {
        setClearTrigger((prev) => !prev);
    };

    const saveImageConfiguration = async (
        imageFile: FileRecord | undefined,
        size: ImageSize,
        position: ImagePosition
    ): Promise<void> => {
        if (imageBlockConfiguration.id == null) {
            return;
        }

        onBlockSaving();

        try {
            const requestData: ImageBlockConfigurationUpdatePutParams = {
                imageFileId: imageFile?.id,
                size: size,
                position: position,
            };
            const pathParams: ImageBlockConfigurationUpdatePathParams = {
                configurationId: imageBlockConfiguration.id,
            };

            const response = await updateConfigurationApi(
                requestData,
                pathParams
            );

            setImageFile(imageFile);
            setSaveState(SaveState.Saving);

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    "No response provided when updating image configuration"
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            const updatedConfiguration = response.resultObject.with({
                imageFileId: imageFile?.id,
                imageFile: imageFile,
                size: size,
                position: position,
            });

            const updatedBlock = block.with({
                configuration: updatedConfiguration,
            });

            onBlockSaved(updatedBlock);
        } catch (error) {
            console.error(error);
            onBlockSaved(block);
        }
    };

    const handleSaveImageFile = async (imageFile: FileRecord | undefined) => {
        setImageFile(imageFile);
        await saveImageConfiguration(
            imageFile,
            sizeOrDefault,
            positionOrDefault
        );
    };

    const handleDone = async () => {
        await saveImageConfiguration(
            imageFile,
            sizeOrDefault,
            positionOrDefault
        );
        onDone?.();
    };

    const handleImageClick = (
        e: React.MouseEvent<HTMLImageElement, MouseEvent>
    ) => {
        if (editMode === EditMode.Annotation) {
            handleStartNewImageAnnotation(e);
        }
    };

    const handleSizeChange = (newSize: ImageSize) => {
        setSize(newSize);
        saveImageConfiguration(imageFile, newSize, positionOrDefault);
    };

    const handlePositionChange = (newPosition: ImagePosition) => {
        setPosition(newPosition);
        saveImageConfiguration(imageFile, sizeOrDefault, newPosition);
    };

    const handleReplaceImage = () => {
        setReplaceImageMode(true);
    };

    const handleDelete = async () => {
        await onDelete(block);
    };

    const handleMoveUp = async () => {
        await onMoveUp(block);
    };

    const handleMoveDown = async () => {
        await onMoveDown(block);
    };

    const setAnnotationMode = () => setEditMode(EditMode.Annotation);
    const resetToImageEditMode = () => setEditMode(EditMode.Image);

    const addOrSwapImageAnnotationInList = async (
        updatedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => {
        if (!updatedImageAnnotation) {
            return;
        }
        const isNewImageAnnotation = !imageBlockConfiguration?.annotations.some(
            (ia) => ia.id === updatedImageAnnotation?.id
        );
        const updatedConfiguration = imageBlockConfiguration.with({
            annotations: isNewImageAnnotation
                ? imageBlockConfiguration.annotations.concat([
                      updatedImageAnnotation,
                  ])
                : imageBlockConfiguration.annotations.map((a) =>
                      a.id === updatedImageAnnotation.id
                          ? updatedImageAnnotation
                          : a
                  ),
        });
        const updatedBlock = block.with({
            configuration: updatedConfiguration,
        });
        onBlockSaved?.(updatedBlock);
        closeImageAnnotationModal();
    };

    const removeImageAnnotationInList = async (
        updatedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => {
        if (!updatedImageAnnotation) {
            return;
        }

        const updatedConfiguration = imageBlockConfiguration.with({
            annotations: imageBlockConfiguration.annotations.filter(
                (a) => a.id !== updatedImageAnnotation.id
            ),
        });
        const updatedBlock = block.with({
            configuration: updatedConfiguration,
        });
        onBlockSaved?.(updatedBlock);
    };

    return (
        <>
            {editMode === EditMode.Image && (
                <>
                    <ImageEditorToolbar
                        hasImageFile={!!imageFile}
                        size={sizeOrDefault}
                        position={positionOrDefault}
                        onSizeChange={handleSizeChange}
                        onPositionChange={handlePositionChange}
                        onReplaceImage={handleReplaceImage}
                        onDone={handleDone}
                        onDelete={handleDelete}
                        onMoveDown={handleMoveDown}
                        onMoveUp={handleMoveUp}
                        setAnnotationMode={setAnnotationMode}
                    />
                </>
            )}

            {editMode === EditMode.Annotation && (
                <ImageAnnotationToolbar onCancel={resetToImageEditMode} />
            )}

            <div
                className={
                    imageFile
                        ? `${CSS_CLASS_NAME} ${editMode === EditMode.Annotation && "-annotate"}`
                        : `${CSS_CLASS_NAME}__upload-display`
                }>
                <ImageBlockUploadField
                    replaceImageMode={replaceImageMode}
                    setReplaceImageMode={setReplaceImageMode}
                    onClearSelectedImage={handleClear}
                    accept={MimeTypes.Image}
                    errorMessage={fileError}
                    fileUploadDestination={FileUploadDestination.NotebookImage}
                    helpText={t("imageBlock-upload-help-text")}
                    isErrored={StringUtils.hasValue(fileError)}
                    maxSize={FileUtils.megabytesToBytes(20)}
                    onFileChanged={handleSaveImageFile}
                    onUploadStart={() => setFileError("")}
                    value={imageFile}
                    userUploadId={userUploadId}
                    renderPreview={!imageFile}
                    size={sizeOrDefault}
                    position={positionOrDefault}
                    onImageAnnotationMoved={addOrSwapImageAnnotationInList}
                    onImageClick={handleImageClick}
                    onImageAnnotationRemoved={removeImageAnnotationInList}
                    onImageAnnotationSaved={addOrSwapImageAnnotationInList}
                    imageAnnotations={imageBlockConfiguration.annotations}
                />
            </div>
            <ImageAnnotationsModal
                number={imageBlockConfiguration.annotations.length + 1}
                imageAnnotation={activeImageAnnotation}
                onClose={closeImageAnnotationModal}
                onSubmit={addOrSwapImageAnnotationInList}
                isVisible={showImageAnnotationModal}
                onCancel={closeImageAnnotationModal}
            />
        </>
    );
};

export default ImageBlockEditor;
